/**
 * card-inline-add.js
 *
 * On-card colour + size add to cart.
 *
 * Cart handling is NOT re-implemented here. Clicking a size writes the variant
 * id into the card's hidden <product-form> and submits it, so the theme's own
 * product-form.js performs the /cart/add.js request with the section ids its
 * cart drawer actually expects, and calls the drawer's own render method.
 *
 * Only if <product-form> is unavailable does this fall back to a direct fetch,
 * and that fallback re-renders the cart drawer section from the server itself
 * rather than assuming a Dawn-style renderContents() contract.
 */

(() => {
  if (customElements.get('card-inline-add')) return;

  const variantCache = new Map();

  function fetchVariants(productUrl) {
    if (variantCache.has(productUrl)) return variantCache.get(productUrl);

    const request = fetch(`${productUrl}.js`, { headers: { Accept: 'application/json' } })
      .then((response) => {
        if (!response.ok) throw new Error(`Product fetch failed: ${response.status}`);
        return response.json();
      })
      .then((product) =>
        product.variants.map((variant) => ({
          id: variant.id,
          o: [variant.option1, variant.option2, variant.option3].filter((value) => value !== null),
          a: variant.available,
          p: variant.price,
          cp: variant.compare_at_price || 0,
        }))
      )
      .catch((error) => {
        console.error('[card-inline-add]', error);
        variantCache.delete(productUrl);
        return null;
      });

    variantCache.set(productUrl, request);
    return request;
  }

  /**
   * Shared mobile bottom sheet.
   *
   * One instance on document.body, reused by every card. It cannot live inside
   * the card: cards sit in sliders with overflow:hidden and transforms, which
   * would clip a fixed-position panel and break its positioning.
   */
  const MOBILE_QUERY = '(max-width: 749px)';

  const sheet = {
    element: null,
    owner: null,

    isMobile() {
      return window.matchMedia(MOBILE_QUERY).matches;
    },

    build() {
      if (this.element) return this.element;

      const root = document.createElement('div');
      root.className = 'inline-add-sheet';
      root.setAttribute('role', 'dialog');
      root.setAttribute('aria-modal', 'true');
      root.hidden = true;
      root.innerHTML = [
        '<div class="inline-add-sheet__overlay" data-sheet-close></div>',
        '<div class="inline-add-sheet__panel">',
        '<div class="inline-add-sheet__handle"></div>',
        '<p class="inline-add-sheet__title" data-sheet-title></p>',
        '<div class="inline-add-sheet__list" data-sheet-list></div>',
        '</div>',
      ].join('');

      document.body.appendChild(root);

      root.addEventListener('click', (event) => {
        if (event.target.closest('[data-sheet-close]')) {
          this.close();
          return;
        }

        const row = event.target.closest('[data-sheet-size]');
        if (!row || row.disabled || !this.owner) return;

        const source = this.owner.sizeButtons.find(
          (button) => button.dataset.value === row.dataset.value
        );
        if (!source || !source.dataset.variantId) return;

        this.owner.addToCart(source.dataset.variantId, source);
        this.close();
      });

      document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') this.close();
      });

      this.element = root;
      return root;
    },

    open(element) {
      const root = this.build();
      this.owner = element;

      root.querySelector('[data-sheet-title]').textContent = element.dataset.productTitle || '';

      const list = root.querySelector('[data-sheet-list]');
      list.innerHTML = '';

      element.sizeButtons.forEach((button) => {
        const row = document.createElement('button');
        row.type = 'button';
        row.className = 'inline-add-sheet__size';
        row.dataset.sheetSize = '';
        row.dataset.value = button.dataset.value;
        row.textContent = button.dataset.value;
        if (button.disabled) {
          row.disabled = true;
          row.classList.add('is-soldout');
        }
        list.appendChild(row);
      });

      root.hidden = false;
      // Next frame so the opening transition actually runs.
      window.requestAnimationFrame(() => root.classList.add('is-open'));
      document.body.classList.add('inline-add-sheet-open');
    },

    close() {
      if (!this.element || this.element.hidden) return;
      this.element.classList.remove('is-open');
      document.body.classList.remove('inline-add-sheet-open');
      this.owner = null;
      window.setTimeout(() => {
        if (this.element && !this.element.classList.contains('is-open')) {
          this.element.hidden = true;
        }
      }, 250);
    },
  };

  function formatMoney(cents, format) {
    if (typeof cents !== 'number') return '';
    const placeholder = /\{\{\s*(\w+)\s*\}\}/;
    const match = format.match(placeholder);
    const style = match ? match[1] : 'amount';

    const withDelimiters = (value, precision, thousands, decimal) => {
      const number = (value / 100.0).toFixed(precision);
      const parts = number.split('.');
      const integer = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, `$1${thousands}`);
      return parts[1] ? `${integer}${decimal}${parts[1]}` : integer;
    };

    let value;
    switch (style) {
      case 'amount_no_decimals':
        value = withDelimiters(cents, 0, ',', '.');
        break;
      case 'amount_with_comma_separator':
        value = withDelimiters(cents, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        value = withDelimiters(cents, 0, '.', ',');
        break;
      case 'amount_no_decimals_with_space_separator':
        value = withDelimiters(cents, 0, ' ', ',');
        break;
      case 'amount_with_space_separator':
        value = withDelimiters(cents, 2, ' ', ',');
        break;
      case 'amount_with_apostrophe_separator':
        value = withDelimiters(cents, 2, "'", '.');
        break;
      default:
        value = withDelimiters(cents, 2, ',', '.');
    }

    return format.replace(placeholder, value);
  }

  class CardInlineAdd extends HTMLElement {
    constructor() {
      super();
      this.onSizeClick = this.onSizeClick.bind(this);
      this.onCardClick = this.onCardClick.bind(this);
      this.onToggleClick = this.onToggleClick.bind(this);
      this.onSheetTriggerClick = this.onSheetTriggerClick.bind(this);
      this.onDocumentClick = this.onDocumentClick.bind(this);
    }

    connectedCallback() {
      // Generic hook first so non-theme cards (e.g. the product tabs section)
      // can scope the element without borrowing theme class names.
      this.card =
        this.closest('[data-inline-add-root]') || this.closest('.card-wrapper') || this.parentElement;
      this.mode = this.dataset.mode || 'overlay';
      this.productUrl = this.dataset.productUrl;
      this.colorIndex = parseInt(this.dataset.colorIndex, 10);
      this.sizeIndex = parseInt(this.dataset.sizeIndex, 10);
      this.selectedColor = this.dataset.selectedColor || '';
      this.updatePriceEnabled = this.dataset.updatePrice === 'true';

      this.labelText = this.querySelector('[data-inline-add-label-text]');
      // Feedback lives in its own layer — the label text is never rewritten, so
      // no failure path can leave the button blank.
      this.feedbackEl = this.querySelector('[data-inline-add-feedback]');
      this.spinner = this.querySelector('.card-inline-add__label .loading__spinner');

      this.productForm = this.querySelector('product-form');
      this.form = this.querySelector('form[data-type="add-to-cart-form"]');
      this.variantInput = this.querySelector('[data-inline-add-variant-input]');
      this.formSubmit = this.querySelector('[data-inline-add-submit]');

      this.variants = this.readInlineVariants();

      this.addEventListener('click', this.onSizeClick);

      this.sizesTrack = this.querySelector('[data-inline-add-sizes]');
      this.labelEl = this.querySelector('[data-inline-add-label]');

      // Narrow cards can't fit the label and every size. Drop the label rather
      // than let sizes scroll out of sight — the sizes are the point.
      this.fit();
      if (window.ResizeObserver) {
        this.resizeObserver = new ResizeObserver(() => this.fit());
        this.resizeObserver.observe(this);
      }

      this.sheetTrigger = this.querySelector('[data-inline-add-sheet-trigger]');
      if (this.sheetTrigger) {
        this.sheetTrigger.addEventListener('click', this.onSheetTriggerClick);
      }

      // Returning via bfcache restores the DOM exactly as it was left, which can
      // be mid-feedback. Repaint the label on the way back in.
      this.onPageShow = () => this.restoreLabel();
      window.addEventListener('pageshow', this.onPageShow);

      if (this.mode === 'expand') {
        this.toggle = this.querySelector('[data-inline-add-toggle]');
        if (this.toggle) {
          this.toggle.addEventListener('click', this.onToggleClick);
        }
        document.addEventListener('click', this.onDocumentClick);
      }

      if (this.colorIndex >= 0 && this.card) {
        // Capture phase: run before the theme's own swatch handler and stop it.
        this.card.addEventListener('click', this.onCardClick, true);
      }
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.onSizeClick);
      window.removeEventListener('pageshow', this.onPageShow);
      if (this.resizeObserver) this.resizeObserver.disconnect();
      if (this.sheetTrigger) this.sheetTrigger.removeEventListener('click', this.onSheetTriggerClick);
      if (sheet.owner === this) sheet.close();
      if (this.toggle) this.toggle.removeEventListener('click', this.onToggleClick);
      document.removeEventListener('click', this.onDocumentClick);
      if (this.card) this.card.removeEventListener('click', this.onCardClick, true);
    }

    readInlineVariants() {
      const script = this.querySelector('[data-inline-add-variants]');
      if (!script) return null;
      try {
        return JSON.parse(script.textContent);
      } catch (error) {
        console.error('[card-inline-add] Could not parse variant data', error);
        return null;
      }
    }

    get sizeButtons() {
      return Array.from(this.querySelectorAll('[data-inline-add-size]'));
    }

    /* ---------------------------------------------------------------- colour */

    onCardClick(event) {
      const swatch = event.target.closest('[data-inline-add-color]');
      if (!swatch || !this.card.contains(swatch)) return;

      event.preventDefault();
      event.stopPropagation();

      const value = swatch.dataset.value;
      if (!value || value === this.selectedColor) return;

      this.selectColor(value, swatch);
    }

    async selectColor(value, swatch) {
      this.selectedColor = value;
      this.dataset.selectedColor = value;

      this.card.querySelectorAll('[data-inline-add-color]').forEach((button) => {
        button.classList.toggle('active', button === swatch);
        button.setAttribute('aria-pressed', button === swatch ? 'true' : 'false');
      });

      this.swapImage(swatch);

      const variants = await this.ensureVariants();
      if (!variants) return;

      this.renderSizes(variants);
      if (this.updatePriceEnabled) this.renderPrice(variants);
    }

    async ensureVariants() {
      if (this.variants) return this.variants;
      this.classList.add('is-loading');
      const variants = await fetchVariants(this.productUrl);
      this.classList.remove('is-loading');
      if (variants) this.variants = variants;
      return variants;
    }

    swapImage(swatch) {
      if (!swatch || !swatch.dataset.src) return;

      const primary =
        this.card.querySelector('[data-inline-add-image]') ||
        this.card.querySelector('.product-first-image img');
      if (primary) {
        if (swatch.dataset.srcset) primary.setAttribute('srcset', swatch.dataset.srcset);
        primary.setAttribute('src', swatch.dataset.src);
      }

      // The hover shot lives in a separate <img>; without this it keeps showing
      // the back of whichever colour was rendered server-side.
      const mediaInner =
        this.card.querySelector('[data-inline-add-media]') ||
        this.card.querySelector('.card-product__media-inner');
      const secondary =
        this.card.querySelector('[data-inline-add-image-secondary]') ||
        this.card.querySelector('.product-secondary-image img');
      const secondarySrc = swatch.dataset.srcSecondary;

      if (secondary && secondarySrc) {
        if (swatch.dataset.srcsetSecondary) {
          secondary.setAttribute('srcset', swatch.dataset.srcsetSecondary);
        }
        secondary.setAttribute('src', secondarySrc);
        if (mediaInner) {
          mediaInner.classList.add('card-product__media-secondary-image');
          mediaInner.classList.remove('inline-add-no-hover');
        }
      } else if (mediaInner) {
        // No known back image for this colour — disable the hover swap rather
        // than reveal another colour's photo. Both class names are emitted so
        // theme cards and custom sections can each style the disabled state.
        mediaInner.classList.remove('card-product__media-secondary-image');
        mediaInner.classList.add('inline-add-no-hover');
      }
    }

    matchVariant(variants, sizeValue) {
      return variants.find((variant) => {
        if (variant.o[this.sizeIndex] !== sizeValue) return false;
        if (this.colorIndex >= 0 && variant.o[this.colorIndex] !== this.selectedColor) return false;
        return true;
      });
    }

    renderSizes(variants) {
      let anyAvailable = false;

      this.sizeButtons.forEach((button) => {
        const variant = this.matchVariant(variants, button.dataset.value);
        const available = Boolean(variant && variant.a);

        button.dataset.variantId = variant ? variant.id : '';
        button.disabled = !available;
        button.classList.toggle('is-soldout', !available);
        button.classList.remove('is-selected');
        if (available) {
          button.removeAttribute('aria-disabled');
          anyAvailable = true;
        } else {
          button.setAttribute('aria-disabled', 'true');
        }
      });

      this.classList.toggle('is-unavailable', !anyAvailable);

      // Chip set may have changed width (different sizes for this colour).
      this.fit();
    }

    renderPrice(variants) {
      const colorVariants = variants.filter(
        (variant) => this.colorIndex < 0 || variant.o[this.colorIndex] === this.selectedColor
      );
      if (!colorVariants.length) return;

      const variant = colorVariants.find((item) => item.a) || colorVariants[0];
      if (typeof variant.p !== 'number') return;

      const priceBlock =
        this.card.querySelector('[data-inline-add-price]') || this.card.querySelector('.price');
      if (!priceBlock) return;

      const format = this.dataset.moneyFormat || '{{amount}}';
      const onSale = variant.cp > variant.p;

      priceBlock.classList.toggle('price--on-sale', onSale);

      // Adjust these selectors if your price snippet uses different class names.
      const custom = priceBlock.querySelector('[data-inline-add-price-current]');
      if (custom) {
        custom.textContent = formatMoney(variant.p, format);
        const customCompare = priceBlock.querySelector('[data-inline-add-price-compare]');
        if (customCompare) {
          customCompare.textContent = onSale ? formatMoney(variant.cp, format) : '';
          customCompare.hidden = !onSale;
        }
        return;
      }

      const regular = priceBlock.querySelector('.price__regular .price-item--regular');
      const sale = priceBlock.querySelector('.price__sale .price-item--sale');
      const compare = priceBlock.querySelector('.price__sale .price-item--regular');

      if (regular) regular.textContent = formatMoney(variant.p, format);
      if (sale) sale.textContent = formatMoney(variant.p, format);
      if (compare) compare.textContent = onSale ? formatMoney(variant.cp, format) : '';
    }

    /* ------------------------------------------------------------------ cart */

    /* ------------------------------------------------------------------- fit */

    /**
     * Hide the label when the size chips would overflow. Batched into a frame
     * and made idempotent so toggling the class (which changes our own size)
     * can't drive the ResizeObserver into a loop.
     */
    fit() {
      if (this.mode !== 'overlay' || !this.sizesTrack || !this.labelEl) return;
      if (this.fitPending) return;
      this.fitPending = true;

      window.requestAnimationFrame(() => {
        this.fitPending = false;
        if (!this.isConnected) return;

        const wasCompact = this.classList.contains('is-compact');

        // Measure with the label present, whatever the current state.
        if (wasCompact) this.classList.remove('is-compact');
        const overflows = this.sizesTrack.scrollWidth > this.sizesTrack.clientWidth + 1;

        if (overflows !== wasCompact) {
          this.classList.toggle('is-compact', overflows);
        } else if (wasCompact) {
          this.classList.add('is-compact');
        }
      });
    }

    /* ---------------------------------------------------------------- expand */

    onSheetTriggerClick(event) {
      event.preventDefault();
      event.stopPropagation();
      this.openSheet();
    }

    async openSheet() {
      // Availability must be current before the sheet lists sizes.
      const variants = await this.ensureVariants();
      if (variants) this.renderSizes(variants);
      sheet.open(this);
    }

    onToggleClick(event) {
      event.preventDefault();
      event.stopPropagation();

      // On mobile the inline chips are too small to tap — use the sheet.
      if (sheet.isMobile()) {
        this.openSheet();
        return;
      }

      this.setOpen(!this.classList.contains('is-open'));
    }

    onDocumentClick(event) {
      if (this.contains(event.target)) return;
      this.setOpen(false);
    }

    setOpen(open) {
      this.classList.toggle('is-open', open);
      if (this.toggle) this.toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
    }

    onSizeClick(event) {
      const button = event.target.closest('[data-inline-add-size]');
      if (!button || button.disabled) return;

      event.preventDefault();
      event.stopPropagation();

      const variantId = button.dataset.variantId;
      if (!variantId) return;

      this.addToCart(variantId, button);
    }

    addToCart(variantId, button) {
      if (this.classList.contains('is-adding')) return;

      this.classList.add('is-adding');
      button.classList.add('is-selected');
      if (this.spinner) this.spinner.classList.remove('hidden');

      const canUseThemeForm =
        this.form && this.variantInput && this.formSubmit && customElements.get('product-form');

      if (canUseThemeForm) {
        this.submitThemeForm(variantId, button);
      } else {
        this.submitDirect(variantId, button);
      }
    }

    /**
     * Preferred path: hand the add off to the theme's own <product-form>.
     * Its own JS knows which cart sections to request and how to render them.
     */
    submitThemeForm(variantId, button) {
      this.variantInput.value = variantId;
      this.variantInput.disabled = false;

      // product-form.js toggles .loading on its submit button for the duration
      // of the request; watch that to know when the add has finished.
      const observer = new MutationObserver(() => {
        if (this.formSubmit.classList.contains('loading')) return;
        observer.disconnect();
        this.finishAdd(button, this.querySelector('.product-form__error-message-wrapper:not(.hidden)'));
      });
      observer.observe(this.formSubmit, { attributes: true, attributeFilter: ['class'] });

      // Safety net in case the theme never sets .loading.
      window.setTimeout(() => {
        observer.disconnect();
        this.finishAdd(button, null);
      }, 4000);

      if (typeof this.form.requestSubmit === 'function') {
        this.form.requestSubmit(this.formSubmit);
      } else {
        this.formSubmit.click();
      }
    }

    /**
     * Fallback only. Adds, then re-renders the cart drawer / bubble sections
     * from the server so the drawer never paints an empty item list.
     */
    async submitDirect(variantId, button) {
      try {
        const addResponse = await fetch(`${window.routes?.cart_add_url || '/cart/add'}.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/javascript' },
          body: JSON.stringify({ items: [{ id: Number(variantId), quantity: 1 }] }),
        });

        const data = await addResponse.json();
        if (data.status) {
          this.finishAdd(button, data.description || data.message);
          return;
        }

        await this.refreshCartSections();

        const drawer = document.querySelector('cart-drawer');
        if (drawer && typeof drawer.open === 'function') drawer.open();

        if (typeof window.publish === 'function' && window.PUB_SUB_EVENTS?.cartUpdate) {
          window.publish(window.PUB_SUB_EVENTS.cartUpdate, {
            source: 'card-inline-add',
            productVariantId: variantId,
          });
        }

        this.finishAdd(button, null);
      } catch (error) {
        console.error('[card-inline-add]', error);
        this.finishAdd(button, this.dataset.errorText || 'Try again');
      }
    }

    /**
     * Re-render every cart-related Shopify section currently in the DOM.
     * Works regardless of what the drawer's JS API is called.
     */
    async refreshCartSections() {
      const sections = Array.from(document.querySelectorAll('[id^="shopify-section-"]'))
        .map((element) => element.id.replace('shopify-section-', ''))
        .filter((id) => /cart/i.test(id));

      if (!sections.length) return;

      const response = await fetch(`${window.location.pathname}?sections=${sections.join(',')}`);
      const rendered = await response.json();

      sections.forEach((id) => {
        const target = document.getElementById(`shopify-section-${id}`);
        if (target && rendered[id]) target.innerHTML = rendered[id];
      });
    }

    finishAdd(button, errorMessage) {
      this.classList.remove('is-adding');
      if (this.spinner) this.spinner.classList.add('hidden');
      window.setTimeout(() => button.classList.remove('is-selected'), 1200);

      if (this.mode === 'expand' && !errorMessage) this.setOpen(false);

      const message = errorMessage
        ? typeof errorMessage === 'string'
          ? errorMessage
          : this.dataset.errorText || 'Try again'
        : this.dataset.addedText || 'Added';

      this.showFeedback(message, Boolean(errorMessage));
    }

    showFeedback(message, isError) {
      if (!this.feedbackEl || !message) return;
      this.feedbackEl.textContent = message;
      this.classList.add('has-feedback');
      this.classList.toggle('is-error', Boolean(isError));
      window.clearTimeout(this.feedbackTimer);
      this.feedbackTimer = window.setTimeout(() => this.restoreLabel(), 1800);
    }

    restoreLabel() {
      this.classList.remove('has-feedback', 'is-error');
      if (this.feedbackEl) this.feedbackEl.textContent = '';
      // Defensive: if the theme never cleared the spinner, some themes hide
      // sibling text while it's visible.
      if (this.spinner) this.spinner.classList.add('hidden');
    }
  }

  customElements.define('card-inline-add', CardInlineAdd);
})();
